import type { MemberType } from '@/components/People/PeopleFilterSelector';
import type { JoinKind } from '@/stores/useJoinIntentStore';
import normalizeInviteCode from './normalizeInviteCode';

export type CurrentInviteLink = {
  code: string;
  kind: JoinKind;
  memberType: MemberType | null;
};

function getMemberType(value: string | null): MemberType | null | undefined {
  if (value === null) {
    return null;
  }

  if (value === 'participant' || value === 'non_participant') {
    return value;
  }

  return undefined;
}

export default function parseCurrentInviteLink(
  link: string,
): CurrentInviteLink | null {
  try {
    const inviteUrl = new URL(link);
    const kind = inviteUrl.pathname.startsWith('/organization/')
      ? 'organization'
      : inviteUrl.pathname.startsWith('/team/')
        ? 'team'
        : null;

    if (!kind || inviteUrl.pathname !== `/${kind}/invite` || inviteUrl.hash) {
      return null;
    }

    const parameterNames = Array.from(inviteUrl.searchParams.keys());
    if (
      parameterNames.some(
        (parameterName) =>
          parameterName !== 'code' && parameterName !== 'memberType',
      ) ||
      inviteUrl.searchParams.getAll('code').length !== 1 ||
      inviteUrl.searchParams.getAll('memberType').length > 1
    ) {
      return null;
    }

    const code = normalizeInviteCode(inviteUrl.searchParams.get('code') || '');
    const memberType = getMemberType(inviteUrl.searchParams.get('memberType'));

    if (!/^[A-Z0-9]{8}$/.test(code) || memberType === undefined) {
      return null;
    }

    return { code, kind, memberType };
  } catch {
    return null;
  }
}

export function isCurrentInviteLink(link: string, publicUrl?: string) {
  if (!publicUrl || !parseCurrentInviteLink(link)) {
    return false;
  }

  try {
    return new URL(link).origin === new URL(publicUrl).origin;
  } catch {
    return false;
  }
}
